import React from 'react'
import { motion } from 'framer-motion'
import { Calendar, Tag, ExternalLink } from 'lucide-react'
import { Article } from '../types'
import { fadeUp, staggerContainer, viewportOnce, cardHover } from '../lib/motion'

interface ArticlesProps {
  articles: Article[]
}

const Articles: React.FC<ArticlesProps> = ({ articles }) => {
  const totalArticles = articles?.length || 0
  const uniqueTags = new Set(articles?.flatMap(a => a.tags || [])).size || 0
  const years = new Set(articles?.map(a => a.date?.slice(0, 4))).size || 0

  return (
    <section id="articles" className="py-20 bg-white dark:bg-gray-900">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Articles
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-4 max-w-2xl mx-auto px-4">
            Writings and technical notes I published to share learnings
          </p>
        </motion.div>

        {/* Article Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
                {totalArticles}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Total Articles</div>
            </div>
          </motion.div>
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-accent-600 dark:text-accent-400 mb-1 sm:mb-2">
                {uniqueTags}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Unique Tags</div>
            </div>
          </motion.div>
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400 mb-1 sm:mb-2">
                {years}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Active Years</div>
            </div>
          </motion.div>
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
                {(articles?.filter(a => a.url)?.length) || 0}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Published Online</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Articles Grid */}
        <motion.div
          className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {articles?.map((article, index) => (
            <motion.article
              key={index}
              className="card p-6 sm:p-8 h-full hover:shadow-xl transition-shadow duration-300 border-l-4 border-primary-500"
              variants={fadeUp}
              whileHover={cardHover.whileHover}
              transition={cardHover.transition}
            >
              {article.image && (
                <div className="mb-4 sm:mb-6 rounded-lg overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-40 sm:h-52 object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white pr-2">
                  {article.title}
                </h3>
                {article.url && (
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    aria-label="Read article"
                    title="Read article"
                  >
                    <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5" />
                  </a>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                {article.date && (
                  <div className="flex items-center mb-1 sm:mb-0">
                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    {new Date(article.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                )}
                {article.tags && article.tags.length > 0 && (
                  <div className="flex items-center">
                    <Tag className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    <span>{article.tags.join(', ')}</span>
                  </div>
                )}
              </div>

              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 leading-relaxed">
                {article.summary}
              </p>

              {article.tags && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-6">
                  {article.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 sm:px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs sm:text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {article.url && (
                <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center"
                  >
                    Read Article
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </a>
                </div>
              )}
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Articles
